import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ivLogo from '@/assets/iv-talent-logomark.png';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { GetStartedDialog } from '@/components/landing/GetStartedDialog';
import { ArrowRight, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

type Step = 'email' | 'code';

export default function SignIn() {
  const [params] = useSearchParams();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState(() => (params.get('email') ?? '').trim().toLowerCase());
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const navigate = useNavigate();

  const requestCode = async (): Promise<boolean> => {
    const { data, error } = await supabase.functions.invoke('request-signin-code', {
      body: { email: email.trim().toLowerCase() },
    });
    if (error || (data && (data as any).error)) {
      const msg = (data as any)?.error ?? error?.message ?? 'Could not send code';
      toast({ title: 'Could not send code', description: msg, variant: 'destructive' });
      return false;
    }
    return true;
  };

  const sendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    const ok = await requestCode();
    setSending(false);
    if (ok) setStep('code');
  };

  const verifyCode = async (token: string) => {
    setVerifying(true);
    const { data, error } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token,
      type: 'email',
    });
    console.log('[auth/signin] verifyOtp response:', { data, error });
    setVerifying(false);
    if (error) {
      console.error('[auth/signin] verifyOtp error:', error);
      toast({ title: 'Invalid code', description: error.message, variant: 'destructive' });
      setCode('');
      return;
    }
    const next = params.get('next');
    const cbHref = next ? `/auth/callback?next=${encodeURIComponent(next)}` : '/auth/callback';
    navigate(cbHref, { replace: true });
  };

  const onCodeChange = (v: string) => {
    setCode(v);
    if (v.length === 8 && !verifying) {
      void verifyCode(v);
    }
  };

  const resend = async () => {
    setSending(true);
    const ok = await requestCode();
    setSending(false);
    if (ok) toast({ title: 'New code sent', description: `Check ${email}.` });
  };

  return (
    <main className="min-h-screen bg-cream text-ink font-grotesk antialiased grid md:grid-cols-12">
      {/* Editorial panel */}
      <aside className="hidden md:flex md:col-span-7 relative flex-col justify-between p-10 lg:p-14 bg-cream-warm border-r border-hairline overflow-hidden">
        <div
          aria-hidden
          className="absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full opacity-[0.08] blur-3xl"
          style={{ background: 'radial-gradient(circle, #E7831D 0%, transparent 70%)' }}
        />
        <Link to="/" aria-label="Home" className="relative inline-flex items-center gap-2.5 group w-fit">
          <img src={ivLogo} alt="IV Talent" className="h-9 w-9 rounded-md" />
          <span className="font-display text-[20px] tracking-tight text-ink">CultureLens</span>
        </Link>

        <div className="relative max-w-[36ch]">
          <h1 className="font-display text-[44px] lg:text-[56px] leading-[1.05] tracking-[-0.025em] text-ink">
            Welcome back.
          </h1>
          <p className="mt-5 text-[16px] lg:text-[17px] text-charcoal/75 leading-[1.6]">
            The signal from real conversations, waiting where you left off.
          </p>
        </div>

        <div className="relative flex items-center justify-between text-[13px] text-charcoal/60">
          <span>New to CultureLens?</span>
          <GetStartedDialog>
            <button className="inline-flex items-center gap-1.5 text-ink hover:text-ink transition-colors font-medium">
              Request access <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </GetStartedDialog>
        </div>
      </aside>

      {/* Form panel */}
      <section className="md:col-span-5 flex flex-col items-center justify-center px-6 py-14 md:py-10">
        <div className="w-full max-w-sm">
          <Link to="/" aria-label="Home" className="md:hidden mb-10 inline-flex items-center gap-2.5">
            <img src={ivLogo} alt="IV Talent" className="h-9 w-9 rounded-md" />
            <span className="font-display text-[18px] tracking-tight text-ink">CultureLens</span>
          </Link>

          <div>
            <h2 className="font-display text-[28px] md:text-[32px] leading-[1.1] tracking-[-0.02em] text-ink">
              Sign in
            </h2>
            <p className="mt-2 text-[14px] text-charcoal/70 leading-relaxed">
              {step === 'email'
                ? 'We’ll email you an 8-digit code. No password.'
                : (
                    <>
                      Enter the 8-digit code sent to{' '}
                      <span className="text-ink">{email}</span>.
                    </>
                  )}
            </p>
          </div>

          <div className="mt-8">
            {step === 'email' ? (
              <form onSubmit={sendCode} className="space-y-4">
                <Input
                  type="email"
                  required
                  autoFocus
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={sending}
                  className="h-12 rounded-full border-hairline bg-cream/60 px-5 text-[15px] focus-visible:ring-1 focus-visible:ring-ink focus-visible:ring-offset-0"
                />
                <Button
                  type="submit"
                  disabled={sending || !email}
                  className="group w-full h-12 rounded-full bg-ink hover:bg-ink-soft text-cream text-[15px] font-medium hover:-translate-y-px transition-transform duration-300"
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Send code <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={8}
                    value={code}
                    onChange={onCodeChange}
                    disabled={verifying}
                    autoFocus
                  >
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                        <InputOTPSlot key={i} index={i} />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                {verifying && (
                  <div className="flex items-center justify-center gap-2 text-[13px] text-charcoal/70">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifying…
                  </div>
                )}
                <div className="flex flex-col gap-1 pt-2 border-t border-hairline">
                  <button
                    type="button"
                    onClick={resend}
                    disabled={sending || verifying}
                    className="w-full py-3 text-[14px] text-charcoal hover:text-ink transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Resend code'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStep('email');
                      setCode('');
                    }}
                    disabled={verifying}
                    className="w-full py-3 text-[14px] text-charcoal hover:text-ink transition-colors disabled:opacity-50"
                  >
                    Use a different email
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="mt-10 md:hidden text-center text-[13px] text-charcoal/65">
            New to CultureLens?{' '}
            <GetStartedDialog>
              <button className="text-ink font-medium hover:text-ink transition-colors">
                Request access
              </button>
            </GetStartedDialog>
          </div>
        </div>
      </section>
    </main>
  );
}
